import React, { useEffect, useRef } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CalendarPlus, LogIn } from "lucide-react";
import { useAuth } from "../../AuthContext";
import { savePulseNext } from "../../pulse/PulseGate";
import { ProposeEventForm } from "./ProposeEventForm";

export const proposalPalette = (dm) => (dm
  ? { card: "bg-[#1a2324]", border: "border-slate-700/50", text: "text-slate-200", sub: "text-slate-300", muted: "text-slate-500", accent: "text-cyan-400",
      input: "bg-[#252e2f] text-slate-200 placeholder:text-slate-600 border-transparent", inputMissing: "bg-amber-400/8 border-amber-400/50 text-slate-200",
      btn: "bg-cyan-400 text-[#001f24] hover:bg-cyan-300", btn2: "bg-[#252e2f] text-slate-300 hover:bg-[#303b3c]",
      tagPage: "bg-emerald-400/12 text-emerald-300", tagUser: "bg-amber-400/12 text-amber-300", soft: "bg-cyan-400/8" }
  : { card: "bg-white", border: "border-[#e7e8e9]", text: "text-[#191c1d]", sub: "text-[#3b494c]", muted: "text-[#6b7a7d]", accent: "text-[#006874]",
      input: "bg-[#f3f4f5] placeholder:text-[#bac9cc] border-transparent", inputMissing: "bg-amber-50 border-amber-300 text-[#191c1d]",
      btn: "bg-[#006874] text-white hover:bg-[#004f58]", btn2: "bg-[#f3f4f5] text-[#3b494c] hover:bg-[#e7e8e9]",
      tagPage: "bg-emerald-50 text-emerald-700", tagUser: "bg-amber-50 text-amber-700", soft: "bg-[#006874]/6" });

/** «Missing an event?» block — anonymous: sign-in CTA (returns here with the form open); members: the proposal form. */
export function ProposeEventBlock({ dm, compact = false }) {
  const { user, loading } = useAuth() || {};
  const c = proposalPalette(dm);
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const ref = useRef(null);
  const wantsOpen = params.get("propose") === "1";

  useEffect(() => {
    if (!wantsOpen || loading) return undefined;
    const go = () => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    go();
    const timers = [800, 2000].map((ms) => setTimeout(go, ms)); // the list above loads async and pushes the block down
    return () => timers.forEach(clearTimeout);
  }, [wantsOpen, loading, user]);

  const signIn = () => {
    const next = `${location.pathname}?propose=1`;
    savePulseNext(next);
    navigate("/auth", { state: { next } });
  };

  if (loading) return null;
  return (
    <section ref={ref} id="propose-event" data-testid="propose-event-block" className={`${compact ? "mt-5" : "mt-12"} rounded-2xl border p-6 sm:p-8 ${c.card} ${c.border}`}>
      <div className="flex items-start gap-3">
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${c.soft}`}><CalendarPlus className={`w-4 h-4 ${c.accent}`} /></span>
        <div className="min-w-0 flex-1">
          <p className={`category-tag text-[10px] ${c.accent}`}>COMMUNITY</p>
          <h2 className={`text-base font-semibold ${c.text}`} data-testid="propose-event-title">
            {user ? "Report a missing event" : "Missing an event?"}
          </h2>
          <p className={`mt-1 text-sm leading-relaxed ${c.muted}`}>
            {user
              ? "Paste the organiser's official page. We read it, pre-fill what it states and only ask you for what's missing. Our editors check every proposal before it goes live."
              : "Know a travel-industry trade show, summit or conference that isn't listed? Members can propose it with just the organiser's URL — our editors verify and publish it."}
          </p>
          {!user && (
            <button onClick={signIn} data-testid="propose-event-signin-btn"
              className={`mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${c.btn}`}>
              <LogIn className="w-3.5 h-3.5" /> Sign in to suggest a missing event
            </button>
          )}
          {!user && (
            <p className={`mt-3 text-[11px] ${c.muted}`}>Free account, magic link, no password — you&apos;ll come straight back here. <Link to="/about" className={`underline ${c.accent}`}>About TourismIntel</Link></p>
          )}
        </div>
      </div>
      {user && <ProposeEventForm dm={dm} c={c} autoFocus={wantsOpen} />}
    </section>
  );
}
